import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { NavLink, Route } from 'react-router-dom';
import { FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Query } from 'react-apollo';
import NeedAdmin from "../NeedAdmin";
import queries from '../../queries';
import { getTheme, toggleTheme, getDensity, setDensity } from '../../userPrefs';
import { t, LANGUAGES, locale, setLocale } from '../../i18n';
import { latestPublicationsLink, interventionsUrl } from "../../config";

class Sidebar extends Component {

  state = { loggedIn: false, prefsTick: 0, prefsOpen: false };

  closePrefs = (e) => {
    if (e && e.type === 'keydown' && e.key !== 'Escape') return;
    if (e && e.type === 'mousedown' && this.prefsRef && this.prefsRef.contains(e.target)) return;
    this.setState({ prefsOpen: false });
  };

  togglePrefs = () => {
    this.setState(({ prefsOpen }) => {
      if (!prefsOpen) {
        document.addEventListener('mousedown', this.closePrefs);
        document.addEventListener('keydown', this.closePrefs);
      } else {
        document.removeEventListener('mousedown', this.closePrefs);
        document.removeEventListener('keydown', this.closePrefs);
      }
      return { prefsOpen: !prefsOpen };
    });
  };

  onPrefsChanged = () => this.setState(({ prefsTick }) => ({ prefsTick: prefsTick + 1 }));

  componentWillUnmount() {
    window.removeEventListener('tsk-prefs-changed', this.onPrefsChanged);
    document.removeEventListener('mousedown', this.closePrefs);
    document.removeEventListener('keydown', this.closePrefs);
  }

  // Pastille utilisateur = bouton qui ouvre le menu « Préférences » (langue, thème, densité).
  renderPreferences() {
    const { identityProvider } = this.props;
    const { prefsOpen } = this.state;
    const user = identityProvider.currentUser();
    const meta = (user && user.user_metadata) || {};
    const name = meta.full_name || (user && user.email) || t('ui.prefs.title');
    const roles = (user && user.app_metadata && user.app_metadata.roles) || [];
    const role = user ? (roles.includes('admin') ? t('ui.role.admin') : roles.includes('no-publish') ? t('ui.role.nopublish') : t('ui.role.editor')) : '';
    const density = getDensity();
    return (
      <div className="tsk-prefs" ref={(el) => { this.prefsRef = el; }}>
        {prefsOpen && (
          <div className="tsk-prefs__pop" role="dialog" aria-label={t('ui.prefs.title')}>
            <div className="tsk-prefs__row">
              <span>{t('ui.nav.language')}</span>
              <div className="tsk-lang-switch" role="group" aria-label={t('ui.nav.language')}>
                {LANGUAGES.map((l) => (
                  <button key={l.code} type="button" className={locale === l.code ? 'is-active' : ''} aria-pressed={locale === l.code} title={l.label} onClick={() => locale !== l.code && setLocale(l.code)}>
                    {l.short}
                  </button>
                ))}
              </div>
            </div>
            <div className="tsk-prefs__row">
              <span>{t('ui.nav.dark')}</span>
              <button type="button" role="switch" aria-checked={getTheme() === 'dark'} aria-label={t('ui.nav.theme_switch')} className={`tsk-switch${getTheme() === 'dark' ? ' is-on' : ''}`} onClick={() => toggleTheme()}>
                <FontAwesomeIcon icon={['fas', 'sun']} className="tsk-switch__icon tsk-switch__icon--sun" />
                <FontAwesomeIcon icon={['fas', 'moon']} className="tsk-switch__icon tsk-switch__icon--moon" />
                <span className="tsk-switch__thumb" />
              </button>
            </div>
            <div className="tsk-prefs__row">
              <span>{t('ui.prefs.density')}</span>
              <div className="tsk-lang-switch" role="group" aria-label={t('ui.prefs.density')}>
                <button type="button" className={density === 'comfortable' ? 'is-active' : ''} aria-pressed={density === 'comfortable'} onClick={() => setDensity('comfortable')}>{t('ui.prefs.comfortable')}</button>
                <button type="button" className={density === 'compact' ? 'is-active' : ''} aria-pressed={density === 'compact'} onClick={() => setDensity('compact')}>{t('ui.prefs.compact')}</button>
              </div>
            </div>
          </div>
        )}
        <button type="button" className="tsk-user-chip tsk-user-chip--button" aria-haspopup="dialog" aria-expanded={prefsOpen} onClick={this.togglePrefs}>
          <span className="tsk-user-chip__avatar">{user ? String(name).trim().slice(0, 1).toUpperCase() : <FontAwesomeIcon icon={['fas', 'cog']} />}</span>
          <span className="tsk-user-chip__text">
            <span className="tsk-user-chip__name">{name}</span>
            {role && <span className="tsk-user-chip__role">{role}</span>}
          </span>
          <FontAwesomeIcon icon={['fas', 'angle-up']} className={`tsk-user-chip__caret${prefsOpen ? ' is-open' : ''}`} />
        </button>
      </div>
    );
  }

  handleLogInClick() {
    const { identityProvider } = this.props;
    identityProvider.open();
  }

  handleLogOutClick() {
    const { identityProvider } = this.props;
    identityProvider.logout();
  }

  afterLogIn() {
    const { identityProvider } = this.props;
    identityProvider.close();
    this.setState({ loggedIn: true });
  }

  componentDidMount() {
    const { identityProvider } = this.props;
    window.addEventListener('tsk-prefs-changed', this.onPrefsChanged);
    identityProvider.on("login", () => this.afterLogIn());
    identityProvider.on("logout", () => this.setState({ loggedIn: false }));
  }

  logInOrOut() {
    const { identityProvider } = this.props;
    const user = identityProvider.currentUser();
    if (!user) {
      return (
        <button type="button" className={'tsk-button tsk-link tsk-nav-link'} onClick={() => this.handleLogInClick()}>
          <FontAwesomeIcon icon={['fas', 'sign-in-alt']} />
          <FormattedMessage id='login.button' />
        </button>
      );
    }
    return (
      <button type="button" className={'tsk-button tsk-link tsk-nav-link'} onClick={() => this.handleLogOutClick()}>
        <FontAwesomeIcon icon={['fas', 'sign-out-alt']} />
        <FormattedMessage id='logout.button' />
      </button>
    );
  }

  render() {
    const { onNavigate } = this.props;
    return (
      <nav aria-label="Main">
        <NavLink exact to="/project" className="tsk-nav-link" activeClassName="tsk-nav-link--active" onClick={onNavigate}>
          <FontAwesomeIcon icon={['fas', 'folder-open']} />
          <FormattedMessage id='projects.title' />
        </NavLink>
        <NavLink exact to="/project/new" className="tsk-nav-link tsk-nav-link--create" activeClassName="tsk-nav-link--active" onClick={onNavigate}>
          <FontAwesomeIcon icon={['fas', 'plus']} />
          {t('ui.nav.new_interface')}
        </NavLink>
        <Route path='/project/:projectId' render={({ match }) => match.params.projectId === 'new' ? null :
          <div className="tsk-nav-sub">
            <Query query={queries.projects.get} variables={{ id: match.params.projectId }}>
              {({ data }) => data && data.project ? (
                <div className="tsk-nav-project-head">
                  <span className="tsk-nav-project-head__label">{t('ui.nav.current')}</span>
                  <span className="tsk-nav-project-head__name">{data.project.name}</span>
                </div>
              ) : null}
            </Query>
            <NavLink
              to={`/project/${match.params.projectId}`}
              className="tsk-nav-link"
              activeClassName="tsk-nav-link--active"
              onClick={onNavigate}
              isActive={(_, location) => {
                const path = location.pathname;
                const base = `/project/${match.params.projectId}`;
                if (!path.startsWith(base)) return false;
                if (path.includes('/tabMenu') || path.includes('/products') || path.includes('/gameprizes') || path.includes('/share')) return false;
                return true;
              }}
            >
              <FontAwesomeIcon icon={['fas', 'file-alt']} />
              <FormattedMessage id='project.pages.title' />
            </NavLink>
            <NeedAdmin>
              <NavLink
                to={`/project/${match.params.projectId}/tabMenu`}
                className="tsk-nav-link"
                activeClassName="tsk-nav-link--active"
                onClick={onNavigate}
              >
                <FontAwesomeIcon icon={['fas', 'bars']} />
                <FormattedMessage id='project.tabmenus.title' />
              </NavLink>
            </NeedAdmin>
            <NavLink
              to={`/project/${match.params.projectId}/products`}
              className="tsk-nav-link"
              activeClassName="tsk-nav-link--active"
              onClick={onNavigate}
            >
              <FontAwesomeIcon icon={['fas', 'box-open']} />
              <FormattedMessage id='project.products.title' />
            </NavLink>
            <NavLink
              to={`/project/${match.params.projectId}/gameprizes`}
              className="tsk-nav-link"
              activeClassName="tsk-nav-link--active"
              onClick={onNavigate}
            >
              <FontAwesomeIcon icon={['fas', 'trophy']} />
              <FormattedMessage id='project.products.gameprize' />
            </NavLink>
            <NavLink
              to={`/project/${match.params.projectId}/share`}
              className="tsk-nav-link"
              activeClassName="tsk-nav-link--active"
              onClick={onNavigate}
            >
              <FontAwesomeIcon icon={['fas', 'share']} />
              {t('ui.share.nav')}
            </NavLink>
          </div>
        } />
        <NeedAdmin trueAdmin>
          <div className="tsk-nav-section-label">Administration</div>
          <NavLink to="/admin/users" className="tsk-nav-link" activeClassName="tsk-nav-link--active" onClick={onNavigate}>
            <FontAwesomeIcon icon={['fas', 'users-cog']} />
            <FormattedMessage id='admin.users.title' />
          </NavLink>
          <NavLink to="/admin/displays" className="tsk-nav-link" activeClassName="tsk-nav-link--active" onClick={onNavigate}>
            <FontAwesomeIcon icon={['fas', 'desktop']} />
            <FormattedMessage id="admin.displays.title" />
          </NavLink>
          <NavLink to="/admin/activity" className="tsk-nav-link" activeClassName="tsk-nav-link--active" onClick={onNavigate}>
            <FontAwesomeIcon icon={['fas', 'history']} />
            {t('ui.activity.title')}
          </NavLink>
          <a href={interventionsUrl} rel="noopener noreferrer" target="_blank" className="tsk-nav-link" onClick={onNavigate}>
            <FontAwesomeIcon icon={['fas', 'calendar-alt']} />
            {t('ui.nav.interventions')}
          </a>
          {!!latestPublicationsLink &&
            <a href={latestPublicationsLink} rel="noopener noreferrer" target="_blank" className="tsk-nav-link" onClick={onNavigate}>
              <FontAwesomeIcon icon={['fas', 'external-link-alt']} />
              <FormattedMessage id={'publications.latest.link.label'} />
            </a>
          }
        </NeedAdmin>
        <div className="tsk-sidebar-footer">
          {this.renderPreferences()}
          <button type="button" className="tsk-button tsk-link tsk-nav-link tsk-nav-help" onClick={() => window.dispatchEvent(new Event('tsk-open-help'))}>
            <FontAwesomeIcon icon={['fas', 'question-circle']} />
            {t('ui.nav.help')}
          </button>
          {this.logInOrOut()}
        </div>
      </nav>
    );
  }
}

Sidebar.propTypes = {
  identityProvider: PropTypes.object.isRequired,
};

export default Sidebar;
